import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useContext, useState } from "react";
import { ChatContext } from "../../contexts/chat/context";
import { ChatHistoryItem } from "../../contexts/chat/type";

interface ChatHistoryDropdownProps {
  onViewFullHistory: (item: ChatHistoryItem) => void;
}

export const ChatHistoryDropdown = ({ onViewFullHistory }: ChatHistoryDropdownProps) => {
  const {
    isConnected,
    chatHistory,
    chatHistoryLoading,
    fetchChatHistory,
    loadChatFromHistory,
  } = useContext(ChatContext);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Fetch history when opening dropdown
    if (isConnected) {
      fetchChatHistory(10);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoadChat = (item: ChatHistoryItem) => {
    loadChatFromHistory(item);
    handleClose();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={!isConnected}
        size="small"
        startIcon={<HistoryIcon fontSize="small" />}
        sx={{
          color: isConnected ? "primary.main" : "action.disabled",
          textTransform: "none",
          fontSize: "0.875rem",
        }}
      >
        Past Queries
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Past Queries
          </Typography>
        </Box>

        {chatHistoryLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : chatHistory.length === 0 ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No past queries
            </Typography>
          </Box>
        ) : (
          chatHistory.map((item) => (
            <MenuItem
              key={item.message_id}
              onClick={() => handleLoadChat(item)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                py: 1.5,
                px: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": {
                  borderBottom: "none",
                },
              }}
            >
              <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    mr: 1,
                  }}
                >
                  {truncateText(item.query, 40)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatTimestamp(item.timestamp)}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                {truncateText(item.response, 60)}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                {item.total_sql_queries > 0 && (
                  <Typography variant="caption" sx={{ color: "info.main", fontSize: "0.7rem" }}>
                    {item.total_sql_queries} SQL
                  </Typography>
                )}
                {item.total_charts > 0 && (
                  <Typography variant="caption" sx={{ color: "success.main", fontSize: "0.7rem" }}>
                    {item.total_charts} Chart
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};
